import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2, AlertCircle, Send, Loader2 } from 'lucide-react';

interface JobApplyModalProps {
  jobTitle: string | null;
  onClose: () => void;
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '12px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0',
  fontSize: 14, fontFamily: 'Inter, sans-serif', outline: 'none', background: '#fff', color: '#1a1a1a',
};

// Same PHP endpoint pattern as event-register.php / contact-submit.php.
const JOB_APPLY_API_URL = 'https://reachgroups.co.in/api/job-apply.php';

const isValidIndianPhone = (raw: string): boolean => {
  const digits = raw.replace(/\D/g, '');
  const last10 = digits.slice(-10);
  return /^[6-9]\d{9}$/.test(last10);
};

const isValidEmail = (raw: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw.trim());

const JobApplyModal: React.FC<JobApplyModalProps> = ({ jobTitle, onClose }) => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', resume: '' });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const submit = async () => {
    if (!form.name.trim()) {
      setStatus('error');
      setErrorMsg('Please enter your name.');
      return;
    }
    if (!isValidIndianPhone(form.phone)) {
      setStatus('error');
      setErrorMsg('Please enter a valid 10-digit mobile number.');
      return;
    }
    if (form.email.trim() !== '' && !isValidEmail(form.email)) {
      setStatus('error');
      setErrorMsg('Please enter a valid email address.');
      return;
    }

    setStatus('submitting');
    setErrorMsg('');

    try {
      const res = await fetch(JOB_APPLY_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ position: jobTitle, ...form }),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.success) {
        throw new Error(data?.error || 'Something went wrong. Please try again.');
      }

      setStatus('success');
      setTimeout(() => {
        setStatus('idle');
        setForm({ name: '', phone: '', email: '', resume: '' });
        onClose();
      }, 1800);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  };

  return (
    <AnimatePresence>
      {jobTitle && (
        <motion.div
          className="modal-overlay"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="modal-panel"
            onClick={e => e.stopPropagation()}
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
          >
            <div style={{ background: 'linear-gradient(135deg, #00464f, #005B66, #0B7078)', padding: '28px 28px 22px', borderRadius: '22px 22px 0 0', position: 'relative' }}>
              <button onClick={onClose} aria-label="Close"
                style={{ position: 'absolute', top: 16, right: 16, width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <X size={16} />
              </button>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.75)', letterSpacing: '1px', textTransform: 'uppercase' }}>Job Application</span>
              <h3 style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: 20, color: '#fff', marginTop: 6, paddingRight: 30 }}>{jobTitle}</h3>
            </div>

            <div style={{ padding: 28 }}>
              {status === 'success' ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <CheckCircle2 size={40} color="#16a34a" style={{ marginBottom: 12 }} />
                  <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: 17, color: '#1a1a1a' }}>Application received!</div>
                  <p style={{ fontSize: 13, color: '#6B7280', marginTop: 6 }}>Our HR team will reach out if there's a fit.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {status === 'error' && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: 12, marginBottom: 10, color: '#991b1b', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <AlertCircle size={16} /> {errorMsg}
                    </div>
                  )}
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Full Name *</label>
                    <input style={inputStyle} placeholder="Your name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Phone Number *</label>
                    <input
                      style={inputStyle} type="tel" inputMode="numeric" placeholder="9XXXXXXXXX" value={form.phone}
                      onChange={e => setForm(p => ({ ...p, phone: e.target.value.replace(/[^\d+]/g, '') }))}
                      maxLength={13}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Email Address</label>
                    <input style={inputStyle} placeholder="you@email.com" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Resume Link</label>
                    <input style={inputStyle} type="text" placeholder="Google Drive / LinkedIn link (optional)" value={form.resume} onChange={e => setForm(p => ({ ...p, resume: e.target.value }))} />
                  </div>
                  <button onClick={submit} disabled={status === 'submitting'} className="btn-primary shine" style={{ justifyContent: 'center', marginTop: 6, padding: 14, opacity: status === 'submitting' ? 0.7 : 1, cursor: status === 'submitting' ? 'not-allowed' : 'pointer' }}>
                    {status === 'submitting' ? (
                      <>Submitting... <Loader2 size={15} className="spin-icon" /></>
                    ) : (
                      <>Submit Application <Send size={15} /></>
                    )}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
          <style>{`
            .spin-icon { animation: spin 0.8s linear infinite; }
            @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          `}</style>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default JobApplyModal;